/**
 * Toast notification system for user feedback
 * Provides non-intrusive notifications for success, error, warning, and info messages
 */

const TOAST_DURATION = 5000; // 5 seconds
const TOAST_CONTAINER_ID = 'toast-container';

/**
 * Ensures the toast container exists in the DOM
 * @returns {HTMLElement} The toast container element
 */
const ensureToastContainer = () => {
  let container = document.getElementById(TOAST_CONTAINER_ID);

  if (!container) {
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');
    document.body.appendChild(container);
  }

  return container;
};

/**
 * Shows a toast notification
 * @param {string} message - Message to display
 * @param {object} options - Toast options
 * @param {string} options.type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {number} options.duration - Display duration in milliseconds
 * @param {boolean} options.dismissible - Whether the toast can be manually dismissed
 */
export const showToast = (
  message,
  { type = 'info', duration = TOAST_DURATION, dismissible = true } = {}
) => {
  if (!message) {
    console.warn('showToast called with empty message');
    return;
  }

  const container = ensureToastContainer();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.setAttribute('role', 'alert');

  const icon = getToastIcon(type);
  const content = document.createElement('div');
  content.className = 'toast-content';
  content.textContent = message;

  toast.appendChild(icon);
  toast.appendChild(content);

  if (dismissible) {
    const closeButton = document.createElement('button');
    closeButton.className = 'toast-close';
    closeButton.setAttribute('aria-label', 'Schließen');
    closeButton.innerHTML = '&times;';
    closeButton.onclick = () => removeToast(toast);
    toast.appendChild(closeButton);
  }

  container.appendChild(toast);

  // Trigger animation
  requestAnimationFrame(() => {
    toast.classList.add('toast-show');
  });

  // Auto-dismiss
  if (duration > 0) {
    setTimeout(() => {
      removeToast(toast);
    }, duration);
  }
};

/**
 * Removes a toast from the DOM
 * @param {HTMLElement} toast - Toast element to remove
 */
const removeToast = (toast) => {
  if (!toast || !toast.parentNode) {
    return;
  }

  toast.classList.remove('toast-show');
  toast.classList.add('toast-hide');

  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 300); // Match CSS transition duration
};

/**
 * Gets the appropriate icon for the toast type
 * @param {string} type - Toast type
 * @returns {HTMLElement} Icon element
 */
const getToastIcon = (type) => {
  const icon = document.createElement('span');
  icon.className = 'toast-icon';
  icon.setAttribute('aria-hidden', 'true');

  switch (type) {
    case 'success':
      icon.innerHTML = `
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      `;
      break;
    case 'error':
      icon.innerHTML = `
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
      `;
      break;
    case 'warning':
      icon.innerHTML = `
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
        </svg>
      `;
      break;
    case 'info':
    default:
      icon.innerHTML = `
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
      `;
      break;
  }

  return icon;
};

/**
 * Shows a success toast
 * @param {string} message - Success message
 * @param {object} options - Toast options
 */
export const showSuccess = (message, options = {}) => {
  showToast(message, { ...options, type: 'success' });
};

/**
 * Shows an error toast
 * @param {string} message - Error message
 * @param {object} options - Toast options
 */
export const showError = (message, options = {}) => {
  showToast(message, { ...options, type: 'error', duration: 7000 }); // Longer duration for errors
};

/**
 * Shows a warning toast
 * @param {string} message - Warning message
 * @param {object} options - Toast options
 */
export const showWarning = (message, options = {}) => {
  showToast(message, { ...options, type: 'warning' });
};

/**
 * Shows an info toast
 * @param {string} message - Info message
 * @param {object} options - Toast options
 */
export const showInfo = (message, options = {}) => {
  showToast(message, { ...options, type: 'info' });
};

/**
 * Clears all toasts
 */
export const clearAllToasts = () => {
  const container = document.getElementById(TOAST_CONTAINER_ID);
  if (container) {
    const toasts = container.querySelectorAll('.toast');
    toasts.forEach((toast) => removeToast(toast));
  }
};
